// Page Template System - Standard page sizes and orientations
// Based on industry standards and research from Canva, Figma, and print specifications

export interface PageTemplate {
  id: string;
  name: string;
  description: string;
  category: 'document' | 'presentation' | 'print' | 'custom' | 'dynamic';
  dimensions: {
    width: number;
    height: number | 'auto'; // Allow auto for dynamic height
  };
  orientation: 'portrait' | 'landscape';
  aspectRatio: string;
  dpi: number;
  isDefault?: boolean;
  isDynamic?: boolean; // Flag for flexible height pages
}

export interface PageSettings {
  templateId: string;
  customDimensions?: {
    width: number;
    height: number;
  };
  orientation: 'portrait' | 'landscape';
  backgroundColor: string;
  showGrid: boolean;
  gridSize: number;
  snapToGrid: boolean;
}

// Standard page templates based on research
// All dimensions are in pixels at 96 DPI (modern web standard)
export const PAGE_TEMPLATES: PageTemplate[] = [
  // Document Templates
  {
    id: 'a4-portrait',
    name: 'A4 Portrait',
    description: '210 × 297 mm (8.3 × 11.7 in)',
    category: 'document',
    dimensions: { width: 794, height: 1123 },
    orientation: 'portrait',
    aspectRatio: '1:1.41',
    dpi: 96,
    isDefault: true,
  },
  {
    id: 'a4-landscape',
    name: 'A4 Landscape',
    description: '297 × 210 mm (11.7 × 8.3 in)',
    category: 'document',
    dimensions: { width: 1123, height: 794 },
    orientation: 'landscape',
    aspectRatio: '1.41:1',
    dpi: 96,
  },
  {
    id: 'letter-portrait',
    name: 'Letter Portrait',
    description: '8.5 × 11 in (216 × 279 mm)',
    category: 'document',
    dimensions: { width: 816, height: 1056 },
    orientation: 'portrait',
    aspectRatio: '1:1.29',
    dpi: 96,
  },
  {
    id: 'letter-landscape',
    name: 'Letter Landscape',
    description: '11 × 8.5 in (279 × 216 mm)',
    category: 'document',
    dimensions: { width: 1056, height: 816 },
    orientation: 'landscape',
    aspectRatio: '1.29:1',
    dpi: 96,
  },
  {
    id: 'legal-portrait',
    name: 'Legal Portrait',
    description: '8.5 × 14 in (216 × 356 mm)',
    category: 'document',
    dimensions: { width: 816, height: 1344 },
    orientation: 'portrait',
    aspectRatio: '1:1.65',
    dpi: 96,
  },
  {
    id: 'legal-landscape',
    name: 'Legal Landscape',
    description: '14 × 8.5 in (356 × 216 mm)',
    category: 'document',
    dimensions: { width: 1344, height: 816 },
    orientation: 'landscape',
    aspectRatio: '1.65:1',
    dpi: 96,
  },

  // Presentation Templates
  {
    id: 'presentation-16-9',
    name: 'Presentation (16:9)',
    description: 'Widescreen presentation format',
    category: 'presentation',
    dimensions: { width: 1920, height: 1080 },
    orientation: 'landscape',
    aspectRatio: '16:9',
    dpi: 96,
  },
  {
    id: 'presentation-4-3',
    name: 'Presentation (4:3)',
    description: 'Standard presentation format',
    category: 'presentation',
    dimensions: { width: 1024, height: 768 },
    orientation: 'landscape',
    aspectRatio: '4:3',
    dpi: 96,
  },
  {
    id: 'presentation-square',
    name: 'Square Presentation',
    description: 'Instagram-style square format',
    category: 'presentation',
    dimensions: { width: 1080, height: 1080 },
    orientation: 'portrait',
    aspectRatio: '1:1',
    dpi: 96,
  },

  // Print Templates
  {
    id: 'poster-a3-portrait',
    name: 'A3 Poster Portrait',
    description: '297 × 420 mm (11.7 × 16.5 in)',
    category: 'print',
    dimensions: { width: 1123, height: 1587 },
    orientation: 'portrait',
    aspectRatio: '1:1.41',
    dpi: 96,
  },
  {
    id: 'poster-a3-landscape',
    name: 'A3 Poster Landscape',
    description: '420 × 297 mm (16.5 × 11.7 in)',
    category: 'print',
    dimensions: { width: 1587, height: 1123 },
    orientation: 'landscape',
    aspectRatio: '1.41:1',
    dpi: 96,
  },
  {
    id: 'business-card',
    name: 'Business Card',
    description: '3.5 × 2 in (89 × 51 mm)',
    category: 'print',
    dimensions: { width: 336, height: 192 },
    orientation: 'landscape',
    aspectRatio: '1.75:1',
    dpi: 96,
  },

  // Custom Template
  {
    id: 'custom',
    name: 'Custom Size',
    description: 'Define your own dimensions',
    category: 'custom',
    dimensions: { width: 800, height: 600 },
    orientation: 'landscape',
    aspectRatio: 'custom',
    dpi: 96,
  },

  // Dynamic/Flexible Templates
  {
    id: 'dynamic-full-width',
    name: 'Dynamic Full Width',
    description: 'Full width page with auto height (Web)',
    category: 'dynamic',
    dimensions: { width: 1200, height: 'auto' },
    orientation: 'portrait',
    aspectRatio: 'flexible',
    dpi: 96,
    isDynamic: true,
  },
  {
    id: 'dynamic-a4-width',
    name: 'Dynamic A4 Width',
    description: 'A4 width (794px) with flexible height',
    category: 'dynamic',
    dimensions: { width: 794, height: 'auto' },
    orientation: 'portrait',
    aspectRatio: 'flexible',
    dpi: 96,
    isDynamic: true,
  },
  {
    id: 'dynamic-content-width',
    name: 'Dynamic Content Width',
    description: 'Standard content width (800px) with auto height',
    category: 'dynamic',
    dimensions: { width: 800, height: 'auto' },
    orientation: 'portrait',
    aspectRatio: 'flexible',
    dpi: 96,
    isDynamic: true,
  },
  {
    id: 'dynamic-narrow',
    name: 'Dynamic Narrow',
    description: 'Narrow layout (600px) with flexible height',
    category: 'dynamic',
    dimensions: { width: 600, height: 'auto' },
    orientation: 'portrait',
    aspectRatio: 'flexible',
    dpi: 96,
    isDynamic: true,
  },
];

// Helper functions
export const getTemplateById = (id: string): PageTemplate | undefined => {
  return PAGE_TEMPLATES.find(template => template.id === id);
};

export const getTemplatesByCategory = (category: PageTemplate['category']): PageTemplate[] => {
  return PAGE_TEMPLATES.filter(template => template.category === category);
};

export const getDefaultTemplate = (): PageTemplate => {
  return PAGE_TEMPLATES.find(template => template.isDefault) || PAGE_TEMPLATES[0];
};

export const createCustomTemplate = (width: number, height: number): PageTemplate => {
  const orientation = width > height ? 'landscape' : 'portrait';
  const aspectRatio = `${width}:${height}`;
  
  return {
    id: 'custom',
    name: 'Custom Size',
    description: `${width} × ${height} px`,
    category: 'custom',
    dimensions: { width, height },
    orientation,
    aspectRatio,
    dpi: 96,
  };
};

export const flipOrientation = (template: PageTemplate): PageTemplate => {
  return {
    ...template,
    dimensions: {
      width: template.dimensions.height,
      height: template.dimensions.width,
    },
    orientation: template.orientation === 'portrait' ? 'landscape' : 'portrait',
    aspectRatio: template.aspectRatio.split(':').reverse().join(':'),
  };
};

// Validation helpers
export const validateDimensions = (width: number, height: number): { valid: boolean; error?: string } => {
  if (width <= 0 || height <= 0) {
    return { valid: false, error: 'Dimensions must be positive numbers' };
  }
  
  if (width > 5000 || height > 5000) {
    return { valid: false, error: 'Dimensions too large (max 5000px)' };
  }
  
  if (width < 100 || height < 100) {
    return { valid: false, error: 'Dimensions too small (min 100px)' };
  }
  
  return { valid: true };
};

// PDF orientation detection
export const getPDFOrientation = (width: number, height: number): 'portrait' | 'landscape' => {
  return width > height ? 'landscape' : 'portrait';
};

// Convert pixels to mm for PDF generation
export const pixelsToMM = (pixels: number, dpi: number = 96): number => {
  return (pixels * 25.4) / dpi;
};

// Convert mm to pixels for display
export const mmToPixels = (mm: number, dpi: number = 96): number => {
  return (mm * dpi) / 25.4;
};
